import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { usePlaylistPlayer, type PlaylistRepeatMode, type SurahRepeatPlaythrough } from '../hooks/usePlaylistPlayer'
import { usePreferences } from '../preferences/PreferencesContext'
import { useAuth } from '../hooks/useAuth'
import { useAutoDownloadOnPlay } from '../hooks/useAutoDownloadOnPlay'
import type { SurahPlaybackSettings } from '../types'

/**
 * Identity of the playlist that's currently driving global playback. Held in
 * provider state (not derived from route params) so playback survives
 * navigation away from `/playlists/:id`.
 */
export interface ActivePlaylist {
  id: string
  name: string
  surahs: number[]
  /**
   * Optional per-row playback settings keyed by surah number. Absent surahs
   * play with all defaults. Distinct from `repeatMode` (off / all / one),
   * which applies AFTER all individual surah repeats are exhausted.
   */
  settingsBySurah?: Record<number, SurahPlaybackSettings>
}

interface PlaylistAudioContextValue {
  /** Identity of the playlist currently bound to the engine, or null if idle. */
  activePlaylist: ActivePlaylist | null

  /** True when the underlying audio engine is producing sound. */
  isPlaying: boolean
  /** Index into `activePlaylist.surahs` of the surah currently playing. */
  activeSurahIndex: number | null
  /** The actual surah number (1–114) currently playing, or null. */
  activeSurahNumber: number | null
  /** Loaded ayah list for the active surah; empty when idle. */
  currentAyahNumber: number | null
  /** surahNumber of the ACTUAL playing ayah; may differ from activeSurahNumber during the Bismillah preamble. */
  currentAyahSurahNumber: number | null
  /** Arabic text of the active ayah, or null when idle / not yet loaded. */
  currentAyahArabic: string | null
  /** Translation of the active ayah in the user's edition, or null when idle / not yet loaded. */
  currentAyahTranslation: string | null
  /** Arabic of the NEXT queued ayah (immersive "coming up" preview); null at row end. */
  nextAyahArabic: string | null

  /** True while a surah's ayah metadata is being fetched. */
  loading: boolean
  /** Last surah-load or playback error, or null. */
  error: string | null

  /** Current playback time in seconds. */
  currentTime: number
  /** Duration of the active ayah's buffer in seconds. */
  duration: number
  /** Volume in [0, 1]. */
  volume: number
  /** Within-surah continuous-mode flag (passes ayah → ayah without pause). */
  continuousMode: boolean

  /** Repeat mode at end-of-playlist. Persisted to localStorage. */
  repeatMode: PlaylistRepeatMode
  cycleRepeatMode: () => void

  /** Outer surah-repeat playthrough for the active row, or null when hidden. */
  surahRepeatPlaythrough: SurahRepeatPlaythrough | null

  // ----- Bind / detach -----
  /**
   * Bind the engine to a new playlist. Pass `null` to detach (this stops
   * playback and lets a different audio surface — e.g. the reader page —
   * own the audio context). When the same playlist id is passed back-to-back
   * the existing playback is preserved (no restart).
   */
  setActivePlaylist: (playlist: ActivePlaylist | null) => void

  // ----- Transport -----
  togglePlay: () => void
  pause: () => void
  play: () => void
  /** Jump to a specific row in the active playlist. No-op if no active playlist. */
  playSurahAt: (index: number) => void
  /** Start at index 0 of the active playlist. */
  playFromStart: () => void
  /**
   * Jump playback to a specific ayah of the ACTIVE surah's shaped queue
   * (Bismillah prefix / ayah range / per-ayah repeats included). Returns
   * false when nothing is active or the ayah isn't part of the shaped queue.
   */
  playAyahInActiveSurah: (ayahNumber: number) => boolean
  /** Re-fetch + restart the current ayah from its beginning. Used to
   *  apply a reciter change mid-playback. */
  restartCurrentAyah: () => void
  nextSurah: () => void
  prevSurah: () => void
  seek: (time: number) => void
  setVolume: (value: number) => void
  toggleContinuousMode: () => void
}

const PlaylistAudioContext = createContext<PlaylistAudioContextValue | undefined>(
  undefined,
)

/**
 * Shallow value-equality for the per-surah settings map. Used by the
 * setActivePlaylist short-circuit to decide whether the engine has to be
 * re-bound — comparing references would over-trigger because the playlist
 * page rebuilds the literal on every render.
 */
function sameSettingsBySurah(
  a: Record<number, SurahPlaybackSettings> | undefined,
  b: Record<number, SurahPlaybackSettings> | undefined,
): boolean {
  if (a === b) return true
  const aKeys = a ? Object.keys(a) : []
  const bKeys = b ? Object.keys(b) : []
  if (aKeys.length !== bKeys.length) return false
  for (const k of aKeys) {
    if (!b) return false
    const av = a![Number(k)]
    const bv = b[Number(k)]
    if (!bv) return false
    if (
      av.repeats !== bv.repeats ||
      av.perAyahRepeat !== bv.perAyahRepeat ||
      av.ayahFrom !== bv.ayahFrom ||
      av.ayahTo !== bv.ayahTo
    ) {
      return false
    }
  }
  return true
}

interface PlaylistAudioProviderProps {
  children: ReactNode
}

/**
 * App-root provider that hoists `usePlaylistPlayer` so a single audio engine
 * persists across route changes. Mount this INSIDE `<BrowserRouter>` and
 * INSIDE `<AuthProvider>` (it depends on `usePreferences`, which depends on
 * the React tree being mounted), otherwise the context hooks will throw.
 *
 * Consumers:
 *   - `PlaylistDetailPage` calls `setActivePlaylist({ id, name, surahs })`
 *     on row click and drives playback through the transport methods.
 *   - `PlaylistMiniPlayer` (mounted in `Layout`) reads playback state and
 *     renders a fixed bottom mini-player on every route except the playlist
 *     detail page (which already shows its own large player).
 *   - `QuranReaderPage` calls `setActivePlaylist(null)` when its own audio
 *     engine starts a verse, so we don't end up with two simultaneous audio
 *     sources fighting over the speakers.
 */
export function PlaylistAudioProvider({ children }: PlaylistAudioProviderProps) {
  const { prefs } = usePreferences()
  const { user, refreshUser } = useAuth()
  const [activePlaylist, setActivePlaylistState] = useState<ActivePlaylist | null>(null)

  // The hoisted player hook lives here for the lifetime of the app. We pass
  // the active playlist's surah list (or [] when idle); the hook handles
  // loading/playing each surah on demand.
  const player = usePlaylistPlayer({
    surahs: activePlaylist?.surahs ?? [],
    settingsBySurah: activePlaylist?.settingsBySurah,
    reciter: prefs.reciter,
    edition: prefs.edition,
    bismillahBeforeSurah: prefs.bismillahBeforeSurah,
    isAuthenticated: Boolean(user),
    // Refresh the cached profile after a row's listen is recorded so the
    // per-surah "times played" badge on the playlist page updates live.
    onListenRecorded: refreshUser,
  })

  // Ref over `player` so `setActivePlaylist` can stay STABLE across renders.
  // The provider re-renders on every audio tick (currentTime, isPlaying, etc.)
  // — if setActivePlaylist's identity flipped each time, every consumer that
  // listed it as an effect dep (e.g. PlaylistDetailPage) would re-fire its
  // effect 60×/s and call setActivePlaylist again. That render storm was
  // starving React Router's route transition, leaving users stuck on the
  // playlist page after clicking a sidebar nav item.
  const playerRef = useRef(player)
  playerRef.current = player

  // Auto-download the playing surah when the setting is on.
  useAutoDownloadOnPlay({
    surahNumber: player.activeSurahNumber,
    isPlaying: player.audioPlayer.isPlaying,
  })

  const setActivePlaylist = useCallback((playlist: ActivePlaylist | null) => {
    setActivePlaylistState((current) => {
      // Re-binding to the same playlist id is a no-op so the page can call
      // setActivePlaylist freely on re-render without disturbing playback.
      if (current && playlist && current.id === playlist.id) {
        // Update name/surahs/settings in case they changed (rename, reorder,
        // add, or per-row settings edit).
        const sameSettings = sameSettingsBySurah(
          current.settingsBySurah,
          playlist.settingsBySurah,
        )
        if (
          current.name === playlist.name &&
          current.surahs.length === playlist.surahs.length &&
          current.surahs.every((n, i) => n === playlist.surahs[i]) &&
          sameSettings
        ) {
          return current
        }
        return playlist
      }
      return playlist
    })
  }, [])

  // Detach side effect lives here, NOT in the state updater. Reducers must be
  // pure in React 18 (StrictMode runs them twice). When `activePlaylist`
  // transitions to null we stop the engine; when it changes id we stop the
  // previous track for one frame before the new ayahs flow in.
  const prevActiveIdRef = useRef<string | null>(null)
  useEffect(() => {
    const prevId = prevActiveIdRef.current
    const nextId = activePlaylist?.id ?? null
    if (prevId !== null && prevId !== nextId) {
      playerRef.current.stop()
    }
    prevActiveIdRef.current = nextId
  }, [activePlaylist])

  // Stop playback when the user logs out (manually or via AUTH_EXPIRED token
  // refresh failure). Without this, the engine keeps the audio sources alive
  // even though the page redirects to /auth — the user lands on the login
  // screen with the previous session's surah still recited in the background.
  // We don't gate on `activePlaylist` here: the audio engine may have
  // already been bound to a playlist when the session expired, and even if
  // unbinding fails for any reason, calling stop() is a safe no-op when
  // there's nothing playing.
  useEffect(() => {
    if (user) return
    if (activePlaylist) {
      setActivePlaylistState(null)
    }
    playerRef.current.stop()
  }, [user, activePlaylist])

  const togglePlay = useCallback(() => {
    void player.audioPlayer.togglePlay()
  }, [player.audioPlayer])

  const pause = useCallback(() => {
    player.audioPlayer.pause()
  }, [player.audioPlayer])

  const play = useCallback(() => {
    void player.audioPlayer.togglePlay()
  }, [player.audioPlayer])

  const playSurahAt = useCallback(
    (index: number) => {
      void player.playSurahAt(index)
    },
    [player],
  )

  const playFromStart = useCallback(() => {
    player.playFromStart()
  }, [player])

  // Restart the CURRENT ayah from its beginning. Used when the user
  // changes reciter mid-playback so the new voice takes effect
  // immediately on the in-progress ayah (without this, the engine
  // would only swap voices on the NEXT ayah lookup).
  const restartCurrentAyah = useCallback(() => {
    const ap = player.audioPlayer
    if (ap.currentAyahIndex >= 0) {
      void ap.playAyah(ap.currentAyahIndex)
    }
  }, [player.audioPlayer])

  const value = useMemo<PlaylistAudioContextValue>(() => {
    const ap = player.audioPlayer
    return {
      activePlaylist,
      isPlaying: ap.isPlaying,
      activeSurahIndex: player.activeSurahIndex,
      activeSurahNumber: player.activeSurahNumber,
      currentAyahNumber: player.currentAyahNumber,
      currentAyahSurahNumber: player.currentAyahSurahNumber,
      currentAyahArabic: player.currentAyahArabic,
      currentAyahTranslation: player.currentAyahTranslation,
      nextAyahArabic: player.nextAyahArabic,
      loading: ap.loading || player.loadingSurah,
      error: ap.error || player.error,
      currentTime: ap.currentTime,
      duration: ap.duration,
      volume: ap.volume,
      continuousMode: ap.continuousMode,
      repeatMode: player.repeatMode,
      cycleRepeatMode: player.cycleRepeatMode,
      surahRepeatPlaythrough: player.surahRepeatPlaythrough,
      setActivePlaylist,
      togglePlay,
      pause,
      play,
      playSurahAt,
      playFromStart,
      playAyahInActiveSurah: player.playAyahInActiveSurah,
      restartCurrentAyah,
      nextSurah: player.nextSurah,
      prevSurah: player.prevSurah,
      seek: ap.seek,
      setVolume: ap.setVolume,
      toggleContinuousMode: ap.toggleContinuousMode,
    }
  }, [
    activePlaylist,
    player.activeSurahIndex,
    player.activeSurahNumber,
    player.audioPlayer,
    // The "now playing verse" derived fields. In practice `player.audioPlayer`
    // already changes on every state tick (so the memo rebuilds and picks up
    // the latest derived values via closure), but listing them here is the
    // right defensive move — future code paths that populate the translation
    // ref AFTER audioPlayer settles would otherwise leave the panel showing
    // stale data until the next unrelated re-render.
    player.currentAyahNumber,
    player.currentAyahSurahNumber,
    player.currentAyahArabic,
    player.currentAyahTranslation,
    player.nextAyahArabic,
    player.loadingSurah,
    player.error,
    player.repeatMode,
    player.cycleRepeatMode,
    player.surahRepeatPlaythrough,
    player.nextSurah,
    player.prevSurah,
    player.playFromStart,
    player.playAyahInActiveSurah,
    setActivePlaylist,
    togglePlay,
    pause,
    play,
    playSurahAt,
    playFromStart,
    restartCurrentAyah,
  ])

  return (
    <PlaylistAudioContext.Provider value={value}>
      {children}
    </PlaylistAudioContext.Provider>
  )
}

export function usePlaylistAudio(): PlaylistAudioContextValue {
  const ctx = useContext(PlaylistAudioContext)
  if (!ctx) {
    throw new Error('usePlaylistAudio must be used within a PlaylistAudioProvider')
  }
  return ctx
}

/**
 * Side-effect hook for surfaces that own their OWN audio (e.g. the reader's
 * verse-by-verse player). When the reader engine commits to a play, the
 * global playlist context is detached so the two don't compete for the
 * speaker.
 *
 * NOTE: the `isReaderPlaying` flag should reflect the reader engine's
 * `loading || isPlaying` — NOT just `isPlaying`. The engine sets `loading`
 * synchronously when play() is called but `isPlaying` only flips true after
 * the audio fetch + decode completes (~1-2 s on cold cache). Gating on
 * `isPlaying` alone leaves the playlist running through the load window,
 * producing audible overlap and main-thread contention.
 *
 * Tolerates a missing `PlaylistAudioProvider` ancestor (returns silently) so
 * tests that render the reader in isolation don't have to wire the provider.
 */
export function useDetachPlaylistOnReaderPlay(isReaderPlaying: boolean): void {
  const ctx = useContext(PlaylistAudioContext)
  // Pull the specific fields we need out of ctx instead of depending on ctx
  // itself. The provider's memoized value reconstructs on every audio tick
  // (4Hz) because the playback state inside it changes; listing `ctx` in the
  // effect deps would fire the effect 4×/s for nothing. setActivePlaylist
  // is `useCallback([])`-stable so its identity never changes — the effect
  // now only re-runs when isReaderPlaying or the activePlaylist binding
  // actually transitions.
  const setActivePlaylist = ctx?.setActivePlaylist
  const activePlaylist = ctx?.activePlaylist ?? null

  useEffect(() => {
    if (!setActivePlaylist) return
    if (isReaderPlaying && activePlaylist) {
      setActivePlaylist(null)
    }
  }, [isReaderPlaying, activePlaylist, setActivePlaylist])
}

